import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Camera, MessageSquare, Users, Image, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

function ShareAchievementModal({ isOpen, onClose, weekNumber, dayNumber, isFirstDay, isLastDay }) {
  const navigate = useNavigate();
  
  if (!isOpen) return null;

  const handleShareToCommunity = () => {
    onClose();
    navigate('/community');
  };

  const handleGoToGallery = () => {
    onClose();
    navigate('/gallery');
  };

  const handleSkip = () => {
    onClose();
  };

  // Special content for first day (before photo)
  if (isFirstDay) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="share-modal">
        <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 relative animate-in fade-in zoom-in duration-300">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            data-testid="close-modal-btn"
          >
            <X className="w-6 h-6" />
          </button>
          
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-pink-100 rounded-full mb-4">
              <Camera className="w-8 h-8 text-pink-600" />
            </div>
            <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-2">
              📸 Capture Your "Before" Photo!
            </h2>
            <p className="text-secondary-600">
              Day 1 is the perfect time to document your starting point.
            </p>
          </div>
          
          <div className="bg-pink-50 rounded-xl p-4 mb-6 border border-pink-200">
            <p className="text-secondary-700 text-sm leading-relaxed">
              Take a selfie or ask someone to photograph you today. This is your "before" image—you don't have to share it with anyone. Save it to your <strong>Private Gallery</strong> and watch how much you transform over the next 12 weeks!
            </p>
          </div>
          
          <div className="space-y-3">
            <Button
              onClick={handleGoToGallery}
              className="w-full bg-pink-600 hover:bg-pink-700 text-white py-5 rounded-full"
              data-testid="go-to-gallery-btn"
            >
              <Image className="w-5 h-5 mr-2" />
              Save to My Private Gallery
            </Button>
            
            <Button
              onClick={handleShareToCommunity}
              variant="outline"
              className="w-full border-2 border-purple-300 text-purple-700 hover:bg-purple-50 py-5 rounded-full"
              data-testid="share-to-community-btn"
            >
              <Users className="w-5 h-5 mr-2" />
              Or Share on Community Board
            </Button>
            
            <button
              onClick={handleSkip}
              className="w-full text-secondary-500 hover:text-secondary-700 py-2 text-sm"
              data-testid="skip-btn"
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Special content for last day (after photo + celebration)
  if (isLastDay) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="share-modal-final">
        <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 relative animate-in fade-in zoom-in duration-300">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            data-testid="close-modal-btn"
          >
            <X className="w-6 h-6" />
          </button>
          
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary to-accent rounded-full mb-4">
              <Sparkles className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-2">
              🎉 You Did It! Congratulations! 🎉
            </h2>
            <p className="text-secondary-600">
              You've completed the entire Radiant 40 program!
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-xl p-4 mb-6 border border-primary/20">
            <h3 className="font-bold text-secondary-900 mb-2 flex items-center gap-2">
              <Camera className="w-5 h-5 text-primary" />
              Time for Your "After" Photo!
            </h3>
            <p className="text-secondary-700 text-sm leading-relaxed">
              Remember the "before" photo you took on Day 1? Now it's time to capture your transformation! Take your "after" photo and save it to your gallery. Compare the two and see how far you've come—not just in appearance, but in confidence, vitality, and radiance!
            </p>
          </div>
          
          <div className="space-y-3">
            <Button
              onClick={handleGoToGallery}
              className="w-full bg-gradient-to-r from-primary to-accent hover:opacity-90 text-white py-5 rounded-full"
              data-testid="go-to-gallery-btn"
            >
              <Image className="w-5 h-5 mr-2" />
              Save "After" Photo to Gallery
            </Button>
            
            <Button
              onClick={handleShareToCommunity}
              variant="outline"
              className="w-full border-2 border-purple-300 text-purple-700 hover:bg-purple-50 py-5 rounded-full"
              data-testid="share-to-community-btn"
            >
              <Users className="w-5 h-5 mr-2" />
              Share Your Transformation!
            </Button>
            
            <button
              onClick={handleSkip}
              className="w-full text-secondary-500 hover:text-secondary-700 py-2 text-sm"
              data-testid="skip-btn"
            >
              I'll do this later
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Regular completion modal (all other days)
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="share-modal">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 relative animate-in fade-in zoom-in duration-300">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          data-testid="close-modal-btn"
        >
          <X className="w-6 h-6" />
        </button>
        
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <Sparkles className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-2">
            🌸 Day {dayNumber} Complete!
          </h2>
          <p className="text-secondary-600">
            Amazing work on Week {weekNumber}!
          </p>
        </div>
        
        <div className="bg-purple-50 rounded-xl p-4 mb-6 border border-purple-200">
          <p className="text-secondary-700 text-sm leading-relaxed">
            Would you like to share your achievement with the community? Post a comment about your experience, share a photo, or encourage others on their journey!
          </p>
        </div>
        
        <div className="space-y-3">
          <Button
            onClick={handleShareToCommunity}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white py-5 rounded-full"
            data-testid="share-to-community-btn"
          >
            <MessageSquare className="w-5 h-5 mr-2" />
            Share on Community Board
          </Button>
          
          <Button
            onClick={handleGoToGallery}
            variant="outline"
            className="w-full border-2 border-teal-300 text-teal-700 hover:bg-teal-50 py-5 rounded-full"
            data-testid="go-to-gallery-btn"
          >
            <Camera className="w-5 h-5 mr-2" />
            Add Photos to My Gallery
          </Button>
          
          <button
            onClick={handleSkip}
            className="w-full text-secondary-500 hover:text-secondary-700 py-2 text-sm"
            data-testid="skip-btn"
          >
            Continue to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}

export default ShareAchievementModal;
